import type {
  BackgroundImage,
  BackgroundImageCreateOrUpdateBody,
  BackgroundImageListResponse,
  BackgroundImageUploadResponse,
  BackgroundUpsert,
} from '@/types/backgrounds';
import { ApiService } from './api.service';
import type { SORT_ORDER } from '@/types';

const BASE_URL = '/api/v1/background-images';

class BackgroundsService extends ApiService {
  async list({
    cursor,
    limit,
    sortBy = 'startDate',
    sortOrder = 'ASC',
  }: {
    cursor?: string;
    limit?: number;
    sortBy?: 'startDate';
    sortOrder?: SORT_ORDER | 'ASC' | 'DESC';
  } = {}): Promise<BackgroundImageListResponse> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (typeof limit === 'number') params.set('limit', String(limit));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    const qs = params.toString();
    const { data } = await this.get<BackgroundImageListResponse>(`${BASE_URL}${qs ? `?${qs}` : ''}`);
    return data;
  }

  async getById({ id }: { id: string }): Promise<BackgroundImage> {
    const { data } = await this.get<BackgroundImage>(`${BASE_URL}/${id}`);
    return data;
  }

  async upload({ image }: { image: File }): Promise<string | null> {
    const form = new FormData();
    form.append('image', image);
    const { data } = await this.postForm<BackgroundImageUploadResponse>(`${BASE_URL}/upload`, form);
    return data.filePath ?? null;
  }

  async create({ data }: { data: BackgroundUpsert }): Promise<BackgroundImage> {
    const uploadedPath = data.file ? await this.upload({ image: data.file }) : null;
    const body: BackgroundImageCreateOrUpdateBody = {
      ...data,
      image: {
        path: uploadedPath ?? '',
        removeCurrentImage: data.image.removeCurrentImage,
      },
    };
    const { data: created } = await this.post<BackgroundImage>(`${BASE_URL}`, body);
    return created;
  }

  async update({ id, data }: { id: string; data: BackgroundUpsert }): Promise<BackgroundImage> {
    const uploadedPath = data.file ? await this.upload({ image: data.file }) : null;
    const body: BackgroundImageCreateOrUpdateBody = {
      ...data,
      image: {
        path: uploadedPath ?? data.image.path ?? '',
        removeCurrentImage: false,
      },
      startDate: data.startDate,
      endDate: data.endDate,
    };
    const { data: updated } = await this.put<BackgroundImage>(`${BASE_URL}/${id}`, body);
    return updated;
  }

  async remove({ id }: { id: string }): Promise<void> {
    await this.delete<void>(`${BASE_URL}/${id}`);
  }
}

export const backgroundsService = new BackgroundsService();
